import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Chinese mirror of the Thai corporate-documents-for-abroad guide. */
const TITLE = "泰国公司文件出境使用 — 如何认证才能被对方受理";
const DESC =
  "公司登记证明、股东名册、财务报表与董事会决议各需哪一层认证、正确的办理顺序，以及递交前的核对要点。";
const PATH = "/blog/company-documents-for-abroad";
const URL = `/zh${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "公司文件出境使用需要哪些认证？",
    answer:
      "通常先取得商业发展厅签发的认证副本，必要时由公证律师见证签署，再办理泰国外交部领事司认证与目的地国驻泰使领馆认证。具体层级依受理机关要求而定。",
  },
  {
    question: "公司登记证明（หนังสือรับรอง）需要多新？",
    answer:
      "多数机关要求近期签发，常见为 1 至 3 个月内。银行与投资类审查往往更严格，请以对方书面要求为准。",
  },
  {
    question: "股东名册（บอจ.5）也需要认证吗？",
    answer:
      "涉及股权结构核查（开户、投资、并购、招标）时通常需要，并应与公司登记证明的签发时间接近，以免出现不一致。",
  },
  {
    question: "财务报表需要谁签署？",
    answer:
      "一般需经授权董事签署，并附具审计师签署的审计报告。若对方要求核实签署人身份，则须由公证律师见证。",
  },
  {
    question: "董事会决议应写明什么？",
    answer:
      "会议日期、出席人、决议事项、被授权人姓名与授权范围，以及签署人职衔。授权范围应与实际要办的事项一一对应。",
  },
  {
    question: "翻译需要注意什么？",
    answer:
      "译文须完整对应原件，包括印章、钢印、批注与页码，公司名称与人名拼写须与登记资料及护照一致。",
  },
  {
    question: "常见退件原因有哪些？",
    answer:
      "文件签发时间过久、各份文件间资料不一致、缺少授权链条、认证顺序错误，以及译文遗漏印章文字。",
  },
];

export const Route = createFileRoute("/zh/blog/company-documents-for-abroad")({
  component: ZhCorporateGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "zh" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("company-documents-for-abroad", "zh") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("company-documents-for-abroad") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "泰国公司文件认证, 公司登记证明 认证, บอจ.5 翻译, 董事会决议 领事认证, 泰国财务报表 认证",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "zh"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "首页", url: "/zh" },
              { name: "指南", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("company-documents-for-abroad"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Corporate",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: Array<{ doc: string; layer: string }> = [
  { doc: "公司登记证明（近期签发）", layer: "认证副本 → 领事司 → 使领馆" },
  { doc: "股东名册 บอจ.5", layer: "认证副本 → 领事司 → 使领馆" },
  { doc: "公司章程 / 设立文件", layer: "认证副本 → 领事司（依要求）" },
  { doc: "经审计的财务报表", layer: "董事签署 → 律师见证 → 领事司" },
  { doc: "董事会决议 / 授权书", layer: "律师见证 → 领事司 → 使领馆" },
  { doc: "签署人身份证件与职衔证明", layer: "随主文件一并提交" },
];

const STEPS: string[] = [
  "取得受理机关的书面清单，确认所需文件与时效",
  "向商业发展厅申请近期认证副本",
  "整理授权链条：决议、授权书与签署人身份文件",
  "由公证律师见证需要核实签署的文件",
  "办理泰国外交部领事司认证",
  "办理目的地国驻泰使领馆认证，递交前逐项核对",
];

const CHECKS: string[] = [
  "公司名称在各份文件中的拼写完全一致",
  "签发日期都在受理机关要求的时效内",
  "授权范围与实际要办的事项一一对应",
  "译文包含印章、钢印与批注文字",
  "页数、附件与骑缝章齐全",
];

function ZhCorporateGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/zh" className="text-muted-foreground hover:text-foreground">
          首页
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          指南
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="company-documents-for-abroad" locale="zh" />
      <p className="answer-summary speakable-summary">
        泰国公司文件出境使用，通常先取得商业发展厅的近期认证副本，需要核实签署的文件由公证律师见证，
        再依序办理泰国外交部领事司认证与目的地国驻泰使领馆认证。文件间资料不一致与授权链条缺失，是最常见的退件原因。
      </p>

      <h2>1. 各类文件所需的认证层级</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d.doc}>
            <strong>{d.doc}：</strong> {d.layer}
          </li>
        ))}
      </ul>

      <h2>2. 办理顺序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 递交前的核对要点</h2>
      <ul>
        {CHECKS.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <h2>4. 常见问题</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考来源与相关服务</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.dbd.go.th/" target="_blank" rel="noreferrer">
          泰国商务部商业发展厅
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          泰国外交部领事司
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/zh/corporate" className="text-primary underline">
            公司与商务法律文件服务
          </Link>
        </li>
        <li>
          <Link to="/zh/notary" className="text-primary underline">
            公证律师（Notary Public）服务
          </Link>
        </li>
        <li>
          <Link to="/zh/lgz" className="text-primary underline">
            文件认证与领事认证服务
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容复核日期 {PUBLISHED}。本指南仅为规划用的一般信息，不构成个案法律意见。办理前请向受理机关确认最新要求。
      </p>
    </article>
  );
}
